import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, HelpCircle } from "lucide-react";

export default function FaqCMS() {
  const [faqs, setFaqs] = useState([]);
  const [draft, setDraft] = useState({ q: "", a: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/faqs").then((r) => setFaqs(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.q.trim() || !draft.a.trim()) { toast.error("Enter both a question and answer"); return; }
    setSaving(true);
    try { await api.post("/admin/faqs", draft); toast.success("FAQ added"); setDraft({ q: "", a: "" }); load(); }
    catch { toast.error("Could not add FAQ"); }
    finally { setSaving(false); }
  };
  const update = async (f) => {
    try { await api.put(`/admin/faqs/${f.id}`, { q: f.q, a: f.a, order: f.order }); toast.success("FAQ updated"); }
    catch { toast.error("Could not update"); }
  };
  const remove = async (f) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try { await api.delete(`/admin/faqs/${f.id}`); toast.success("FAQ deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };
  const setField = (id, patch) => setFaqs((list) => list.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  return (
    <div data-testid="faq-cms">
      <div className="mb-5">
        <h3 className="font-serif text-xl font-semibold">FAQ management</h3>
        <p className="text-sm text-muted-foreground">Add, edit or remove the questions shown on the About page. Changes are live immediately.</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="faq-new-form">
        <h4 className="font-serif font-semibold mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-accent" /> Add a new FAQ</h4>
        <div className="space-y-3">
          <div><Label className="mb-1.5 block">Question</Label><Input data-testid="faq-new-q" value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} className="rounded-xl" /></div>
          <div><Label className="mb-1.5 block">Answer</Label><Textarea data-testid="faq-new-a" value={draft.a} onChange={(e) => setDraft({ ...draft, a: e.target.value })} className="rounded-xl min-h-[80px]" /></div>
          <Button onClick={create} disabled={saving} data-testid="faq-add-btn" className="rounded-full bg-primary text-primary-foreground">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add FAQ"}</Button>
        </div>
      </div>

      <div className="space-y-3">
        {faqs.length === 0 && <p className="text-muted-foreground">No FAQs yet.</p>}
        {faqs.map((f) => (
          <div key={f.id} data-testid={`faq-row-${f.id}`} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-start gap-2">
              <HelpCircle className="h-5 w-5 text-accent mt-2 shrink-0" />
              <div className="flex-1 space-y-2">
                <Input data-testid={`faq-q-${f.id}`} value={f.q} onChange={(e) => setField(f.id, { q: e.target.value })} className="rounded-xl font-medium" />
                <Textarea data-testid={`faq-a-${f.id}`} value={f.a} onChange={(e) => setField(f.id, { a: e.target.value })} className="rounded-xl min-h-[70px] text-sm" />
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2"><Label className="text-xs">Order</Label><Input type="number" data-testid={`faq-order-${f.id}`} value={f.order ?? 0} onChange={(e) => setField(f.id, { order: Number(e.target.value) })} className="rounded-xl h-8 w-20" /></div>
                  <Button size="sm" data-testid={`faq-save-${f.id}`} onClick={() => update(f)} className="rounded-full bg-primary text-primary-foreground">Save</Button>
                  <Button size="sm" variant="outline" data-testid={`faq-delete-${f.id}`} onClick={() => remove(f)} className="rounded-full text-destructive border-destructive/40"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
